/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionShow';

const ActionShowManager: ActionManagerFn<'ActionShow'> = (
  platform: Platform,
): ActionManager<'ActionShow'> => {
  return {
    ...baseActionManager(platform, 'ActionShow'),
    defaultValue,
    props: {
      ...baseActionProps,
      targetItemId: {
        label: 'Target Item ID',
        type: 'ItemID',
      },
    },
    name: 'Show UI Component',
    getDisplayText: (item) => item.props.targetItemId,
    icon: { iconName: 'eye' },
    replaceableWith: ['ActionHide'],
    description: `**Overview**<br/>
The Show UI Component action is a visibility control component designed to programmatically reveal a hidden element on the interface. It acts as an imperative "Show" command, setting the visibility state of a target component to true.<br/><br/>
This action is typically used in event-driven workflows where elements—such as success messages, advanced option panels, or loading indicators—need to appear in response to user interactions, rather than relying solely on complex boolean expressions in the component's declarative "Visible" property.

**Key Features**<br/>
* **Universal Targeting:** Unlike specific actions like "Open Drawer" or "Open Modal," this action works on almost any standard UI component (e.g., Containers, Buttons, Text Inputs, Images).
* **Imperative Control:** Allows developers to trigger visibility changes based on events (like a button click or a data fetch completing) rather than static state.
* **Component Interchangeability:** Explicitly configured to be easily swappable with ActionHide, allowing developers to toggle logic quickly (e.g., changing a "Show Details" button to a "Hide Details" button workflow).
* **Visual Context:** The editor displays the targetItemId directly in the action list, making it easy to identify which specific element is being revealed.

**Properties**<br/>
* **Target Item ID (targetItemId):** A property of type ItemID.
  * *Function:* Identifies the unique ID of the UI component to be shown.
  * *Behavior:* When triggered, the targeted component will become visible on the screen if it was previously hidden.

**Use Cases**<br/>
* **Progressive Disclosure:** A "Show Advanced Options" link that triggers this action to reveal a previously hidden container of extra form fields.
* **Feedback Loops:** Revealing a "Success Banner" or "Thank You" message container immediately after a form submission is processed.
* **Loading States:** Showing a "Spinner" or "ProgressBar" component right before starting a long network request (often paired with ActionHide to remove it afterwards).
* **Toggle Interactions:** Used in conjunction with ActionHide to create custom accordions or collapsible sections.`,
  };
};

export default ActionShowManager;
